'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { analytics } from 'lib/analytics/analytics';
import {
  DEFAULT_IDLE_POPUP_DELAY_MS,
  type IdlePopupControls,
  type IdlePopupEvent,
  type IdlePopupTrackingData,
  type UseIdlePopupOptions,
} from './constants';

const pushToDataLayer = (event: string, data?: IdlePopupTrackingData): void => {
  try {
    (
      window as Window & { dataLayer?: Record<string, unknown>[] }
    ).dataLayer?.push({ event, ...data });
  } catch {
    // ignore
  }
};

const getDeviceType = (): string => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

const writeFlag = (key: string): void => {
  try {
    localStorage.setItem(key, 'true');
  } catch {
    // ignore
  }
};

/**
 * Shows a popup once the user has been on the page for `delayMs`. Dismissal and
 * completion persist in localStorage, so it never re-appears across sessions.
 */
export function useIdlePopup({
  storageKeys,
  tracking,
  delayMs = DEFAULT_IDLE_POPUP_DELAY_MS,
  trackingData,
  enabled = true,
}: UseIdlePopupOptions): IdlePopupControls {
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTrackedMount = useRef(false);
  const trackingDataRef = useRef(trackingData);
  const { dismissed: dismissedKey, completed: completedKey } = storageKeys;

  useEffect(() => {
    trackingDataRef.current = trackingData;
  }, [trackingData]);

  const track = useCallback(
    (event: IdlePopupEvent, extra?: IdlePopupTrackingData) => {
      const data = {
        ...trackingDataRef.current,
        page: window.location.pathname,
        device: getDeviceType(),
        ...extra,
      };
      analytics.track(tracking.analytics[event], data);
      pushToDataLayer(tracking.gtm[event], data);
    },
    [tracking],
  );

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    try {
      const wasCompleted = localStorage.getItem(completedKey);
      const wasDismissed = localStorage.getItem(dismissedKey);

      if (wasCompleted || wasDismissed) {
        if (!hasTrackedMount.current) {
          hasTrackedMount.current = true;
          track('SUPPRESSED', {
            delayMs,
            reason: wasCompleted ? 'completed' : 'dismissed',
          });
        }
        return;
      }
    } catch {
      // localStorage unavailable (privacy mode) — proceed
    }

    // Guard against Strict Mode double-mount
    if (!hasTrackedMount.current) {
      hasTrackedMount.current = true;
      track('TIMER_STARTED', { delayMs });
    }

    timerRef.current = setTimeout(() => {
      setIsVisible(true);
      track('POPUP_SHOWN', { delayMs });
    }, delayMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [delayMs, enabled, dismissedKey, completedKey, track]);

  const dismiss = useCallback(() => {
    setIsVisible(false);
    track('POPUP_DISMISSED');
    writeFlag(dismissedKey);
  }, [track, dismissedKey]);

  const complete = useCallback(() => {
    setIsVisible(false);
    track('COMPLETED');
    writeFlag(completedKey);
    writeFlag(dismissedKey);
  }, [track, completedKey, dismissedKey]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
    track('CANCELLED');
  }, [track]);

  return { isVisible, cancel, dismiss, complete };
}
